/* ===========================================================
   Reusable ProductCard / ProductTile / Rating / Quantity
   =========================================================== */
import React from "react";
import { toast } from "@/components/shell";
import { inr } from "@/data/data";
import { Icons } from "@/lib/icons";
import { useWishlist } from "@/hooks/useWishlist";
import { useAddCartItem } from "@/api/hooks";
import { PRODUCT_IMAGES } from "@/lib/images";

const { useState: pcUseState } = React;

function resolveProductImage(product) {
  if (!product) return "/elementor-placeholder-image.png";
  if (product.image_url) return product.image_url;
  if (product.slug && PRODUCT_IMAGES[product.slug]) return PRODUCT_IMAGES[product.slug];
  return "/elementor-placeholder-image.png";
}

function ProductTile({ product, size = "md" }) {
  return (
    <div className={"prod-tile prod-tile-" + size}>
      <img
        className="prod-tile-img"
        src={resolveProductImage(product)}
        alt={product?.name || product?.brand || "Product image"}
        loading="lazy"
      />
    </div>
  );
}

function StarRating({ value = 0, count, size = 12 }) {
  const full = Math.round(value);
  const gold = "#f5b50a";
  return (
    <div className="star-rating" style={{ "--s": size + "px" }}>
      {[1,2,3,4,5].map((i) => (
        <svg key={i} width={size} height={size} viewBox="0 0 20 20" aria-hidden="true">
          <path d="M10 1.5l2.6 5.3 5.9.9-4.2 4.1 1 5.8L10 14.9 4.7 17.6l1-5.8L1.5 7.7l5.9-.9z"
                fill={i <= full ? gold : "none"}
                stroke={i <= full ? gold : "#d6d2c5"}
                strokeWidth="1.5"
                strokeLinejoin="round"/>
        </svg>
      ))}
      {value > 0 && <span className="star-rating-v mono">{value.toFixed(1)}</span>}
      {count != null && <span className="star-rating-c">({count.toLocaleString("en-IN")})</span>}
    </div>
  );
}

function Quantity({ value, onChange, min = 1, max = 99 }) {
  return (
    <div className="qty">
      <button className="qty-btn" disabled={value <= min} onClick={() => onChange(Math.max(min, value - 1))} aria-label="Decrease">
        <Icons.X size={10} stroke={2} style={{ transform: "rotate(45deg)" }} />
      </button>
      <input
        type="number"
        className="qty-input mono"
        value={value}
        onChange={(e) => {
          const n = parseInt(e.target.value, 10);
          if (!isNaN(n)) onChange(Math.min(max, Math.max(min, n)));
        }}
      />
      <button className="qty-btn" disabled={value >= max} onClick={() => onChange(Math.min(max, value + 1))} aria-label="Increase">
        <Icons.Plus size={10} stroke={2} />
      </button>
    </div>
  );
}

const STOCK_PILL = {
  in_stock:     { cls: "pill-emerald", label: "In stock" },
  low_stock:    { cls: "pill-amber",   label: "Low stock" },
  out_of_stock: { cls: "pill-rose",    label: "Out of stock" },
  discontinued: { cls: "pill",         label: "Discontinued" },
};

function ProductCard({ product, layout = "grid" }) {
  const [added, setAdded] = pcUseState(false);
  const p = product;
  const stock = STOCK_PILL[p.stock_status] || STOCK_PILL.in_stock;
  const isOut = p.stock_status === "out_of_stock";
  const priced = p.price_min === p.price_max ? inr(p.price_min) : `${inr(p.price_min)} – ${inr(p.price_max)}`;
  const href = "/pages/storefront/product.html?slug=" + p.slug;
  const wishlist = useWishlist();
  const wished = wishlist.has(p.slug);
  const addToCart = useAddCartItem();
  const toggleWish = (e) => {
    e.preventDefault();
    wishlist.toggle(p.slug);
    toast && toast(wished ? "Removed from wishlist" : "Saved to wishlist");
  };
  const handleAdd = (e) => {
    e.preventDefault();
    addToCart.mutate({ slug: p.slug, qty: 1 }, {
      onSuccess: () => {
        setAdded(true);
        toast && toast("Added to cart");
        setTimeout(() => setAdded(false), 1400);
      },
      onError: (err) => toast && toast(err?.message || "Couldn't add to cart", "error"),
    });
  };

  if (layout === "list") {
    return (
      <article className="prod-card prod-card-list">
        <a href={href} className="prod-card-list-img">
          <ProductTile product={p} size="md" />
          {p.badge && <span className="prod-badge mono">{p.badge}</span>}
        </a>
        <div className="prod-card-list-body">
          <div className="prod-meta-row">
            <span className={"pill " + stock.cls}>{stock.label}</span>
            <span className="mono prod-cat">{p.category_name}</span>
            {p.delivery_time_mins && (
              <span className="prod-delivery-inline mono">
                <Icons.Clock size={11} stroke={2} /> {p.delivery_time_mins} MIN
              </span>
            )}
          </div>
          <a href={href}><h3 className="prod-name prod-name-list">{p.name}</h3></a>
          <div className="prod-sub">
            <span className="prod-brand">{p.brand}</span>
            <span className="prod-dot">·</span>
            <span className="prod-unit">{p.unit_label}</span>
            <span className="prod-dot">·</span>
            <StarRating value={p.rating} count={p.reviews} />
          </div>
        </div>
        <div className="prod-card-list-foot">
          <div className="prod-price-label kicker">UNIT PRICE</div>
          <div className="prod-price">{priced}</div>
          {isOut ? (
            <button className="btn btn-outline btn-sm" disabled>Notify</button>
          ) : (
            <button className={"btn btn-sm prod-add" + (added ? " is-added" : "")} onClick={handleAdd} disabled={addToCart.isPending}>
              {added ? <><Icons.Check size={14} stroke={2.4} /> Added</> : <><Icons.Plus size={14} stroke={2.2} /> Add to cart</>}
            </button>
          )}
        </div>
      </article>
    );
  }

  return (
    <article className="prod-card">
      <a href={href} className="prod-img-link">
        <div className="prod-img">
          <ProductTile product={p} size="md" />
          {p.badge && <span className="prod-badge mono">{p.badge}</span>}
          <button
            className={"prod-wish" + (wished ? " is-active" : "")}
            aria-pressed={wished}
            aria-label={wished ? "Remove from wishlist" : "Save to wishlist"}
            onClick={toggleWish}
            style={wished ? { color: "#e11d48", borderColor: "#e11d48" } : undefined}
          >
            <Icons.Heart size={15} stroke={wished ? 2.4 : 1.7} />
          </button>
          {p.delivery_time_mins && (
            <span className="prod-delivery mono">
              <Icons.Clock size={11} stroke={2} /> {p.delivery_time_mins} MIN
            </span>
          )}
          {isOut && (
            <div className="prod-out">
              <span className="mono">OUT OF STOCK · NOTIFY ME</span>
            </div>
          )}
        </div>
      </a>

      <div className="prod-body">
        <div className="prod-meta-row">
          <span className={"pill " + stock.cls}>{stock.label}</span>
          <span className="mono prod-cat">{p.category_name}</span>
        </div>
        <a href={href}><h3 className="prod-name">{p.name}</h3></a>
        <div className="prod-sub">
          <span className="prod-brand">{p.brand}</span>
          <span className="prod-dot">·</span>
          <span className="prod-unit">{p.unit_label}</span>
        </div>
        <StarRating value={p.rating} count={p.reviews} />

        <div className="prod-foot">
          <div>
            <div className="prod-price-label kicker">UNIT PRICE</div>
            <div className="prod-price">{priced}</div>
          </div>
          {isOut ? (
            <button className="btn btn-outline btn-sm" disabled style={{ opacity: 0.55 }}>Notify</button>
          ) : (
            <button
              className={"btn btn-sm prod-add" + (added ? " is-added" : "")}
              onClick={handleAdd}
              disabled={addToCart.isPending}
            >
              {added ? <><Icons.Check size={14} stroke={2.4} /> Added</> : <><Icons.Plus size={14} stroke={2.2} /> Add</>}
            </button>
          )}
        </div>
      </div>
    </article>
  );
}

export { ProductCard, ProductTile, StarRating, Quantity, STOCK_PILL };

